import refreshIcon from "../../assets/refresh2.svg"
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDebounce } from "../../hooks/useDebounce";
import { useLocation, useNavigate } from "react-router-dom";
import copy from "../../assets/copy.svg";
import { ConfirmModal } from "../../components/ConfirmModal/ConfirmModal";
import {
  ColumnDef,
  DataTable,
  SortOrder,
} from "../../components/DataTable/DataTable";
import { LanguageInfo, useLanguages } from "../../context/LanguagesContext";
import { usePageMetadata } from "../../context/PageMetadataContext";
import { usePortal } from "../../context/PortalsContext";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import {
  addPortal,
  getPortals,
  updatePortal,
} from "../../services/api/apiClient";
import {
  AddSubPortals,
  CreatePortalBodyParams,
  GetPortalsParams,
  Language,
  Portal,
  PortalResultType,
  PortalsResponse,
  UpdatePortalBodyParams,
} from "../../services/api/types/portalConfiguration/portal";
import { RoleLevel, Roles } from "../../services/api/types/users/users";
import { brand, gray } from "../../theme/themePrimitives";
import { REGEX_ALPHANUMERIC, SUB_DOMAIN_REGEX } from "../../utils/constants";
import {
  getFirstAccessiblePortalConfigPath,
  getUserHighestRoleLevel,
  hasAdminAccess,
} from "../../utils/roleUtils";
import { AddNewPortalModal } from "./components/AddNewPortalModal";
import { LanguagesModal } from "./components/LanguagesModal";
import { extractErrorMessage } from "../../utils/errorUtils";
import { MAX_LENGTH_SHORT_LABEL } from "../../utils/inputLimits";
import { useSelectPortal } from "../../hooks/useSelectPortal";

type Subportal = {
  name: string;
  subDomain: string;
  nameError?: string;
  subDomainError?: string;
};

interface PortalConfigurationProps {}

export const PortalConfiguration: React.FC<PortalConfigurationProps> = () => {
  const [portalsResponse, setPortalsResponse] = useState<PortalsResponse>();
  const [portalsArray, setPortalsArray] = useState<Portal[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [languagesModal, setLanguagesModal] = useState(false);
  const [selectedPortal, setSelectedPortal] = useState<Portal | null>(null);
  const [authoringToolModalOpen, setAuthoringToolModalOpen] = useState(false);
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [subPortals, setSubPortals] = useState<Subportal[] | []>([]);
  const [selectedParentId, setSelectedParentId] = useState<string | null>("");
  const [subDomain, setSubDomain] = useState("");
  const [subDomainError, setSubDomainError] = useState("");
  const [authoringTool, setAuthoringTool] = useState(false);
  const [customTranslation, setCustomTranslation] = useState(false);
  const [spoofingEmail, setSpoofingEmail] = useState("");
  const [spoofingEmailError, setSpoofingEmailError] = useState("");
  const [maxRoleLevel, setMaxRoleLevel] = useState<RoleLevel | "">("");
  const [maxRoleLevelError, setMaxRoleLevelError] = useState("");

  const [languagesArray, setLanguagesArray] = useState<Language[]>([]);
  const [languagesError, setLanguagesError] = useState("");
  const [loading, setLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const [page, setPage] = useState(1);
  const [isParent, setIsParent] = useState(false);
  const [isSub, setIsSub] = useState(false);
  const [portalTypeError, setPortalTypeError] = useState("");
  const { showToast } = useToast();
  const [limit, setLimit] = useState(10);
  const [sort, setSort] = useState<
    { orderBy: string; order: SortOrder } | undefined
  >(undefined);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    languages,
    getLanguageDetails,
    dispatch: languagesDispatch,
    fetchLanguages,
  } = useLanguages();
  const {
    portals,
    dispatch,
    fetchPortals: fetchPortalsContext,
  } = usePortal();
  const selectPortal = useSelectPortal();
  const { auth } = useAuth();
  const loaded = !!portalsResponse && !!portals;

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setName(value);

    if (!value) {
      setNameError("Name is required.");
    } else if (value.length < 2) {
      setNameError("Name must be at least 2 characters long.");
    } else if (!REGEX_ALPHANUMERIC.test(value)) {
      setNameError("Name can only contain letters and spaces.");
    } else {
      setNameError("");
    }
  };

  const handleAddSubPortal = () => {
    setSubPortals([
      ...subPortals,
      { name: "", subDomain: "", nameError: "", subDomainError: "" },
    ]);
  };

  const handleSubDomainChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value.toLowerCase();
    setSubDomain(value);

    if (!value) {
      setSubDomainError("Subdomain is required.");
    } else if (value.length < 2) {
      setSubDomainError("Subdomain must be at least 2 characters long.");
    } else if (value.startsWith("-") || value.endsWith("-")) {
      setSubDomainError("Subdomain cannot start or end with a hyphen.");
      setSubDomainError("Subdomain cannot start or end with a hyphen.");
    } else if (value.length && !SUB_DOMAIN_REGEX.test(value)) {
      setSubDomainError(
        "Subdomain can only contain letters, numbers, and hyphens.",
      );
    } else {
      setSubDomainError("");
    }
  };

  const handleSwitchChange = (e: any) => {
    setAuthoringTool(e.target.checked);
  };

  const handleCustomTranslationChange = (e: any) => {
    setCustomTranslation(e.target.checked);
  };

  const handleSpoofingEmailChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    setSpoofingEmail(value);

    // Basic email validation
    if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setSpoofingEmailError("Please enter a valid email address.");
    } else {
      setSpoofingEmailError("");
    }
  };

  const handleLanguagesChange = (_: any, selected: LanguageInfo[]) => {
    setLanguagesError("");
    const selectedLanguages: Language[] = selected
      .filter((lang) => lang.langugageCode !== "en")
      .map((lang) => ({
        languageCode: lang.langugageCode,
      }));
    setLanguagesArray([{ languageCode: "en" }, ...selectedLanguages]);
  };

  const handleRowsChange = (rows: number) => {
    setLimit(rows);
  };

  const latestRequestId = useRef(0);

  const fetchPortals = useCallback(
    async (
      params: GetPortalsParams = { resultType: PortalResultType.GRID },
      options?: { surfaceErrors?: boolean },
    ) => {
      const requestId = ++latestRequestId.current;
      try {
        setLoading(true);
        const response: PortalsResponse = await getPortals(params);
        if (requestId !== latestRequestId.current) return;
        setPortalsResponse(response);
        setPortalsArray(response.rows);
      } catch (error) {
        if (requestId !== latestRequestId.current) return;
        console.log("ERROR: ", error);
        if (options?.surfaceErrors) {
          showToast(
            extractErrorMessage(error, "Failed to refresh portals."),
            "error",
          );
        }
      } finally {
        if (requestId === latestRequestId.current) setLoading(false);
      }
    },
    [showToast],
  );

  useEffect(() => {
    fetchPortals({
      resultType: PortalResultType.GRID,
      ...(debouncedSearchQuery ? { portalName: debouncedSearchQuery } : {}),
      limit,
    });
  }, [debouncedSearchQuery, limit, fetchPortals]);

  useEffect(() => {
    fetchPortalsContext();
  }, [fetchPortalsContext]);

  useEffect(() => {
    setLanguagesArray((prev) => {
      if (prev.find((l) => l.languageCode === "en")) return prev;
      return [...prev, { languageCode: "en" }];
    });
  }, []);

  const handleEditClick = useCallback(
    (portalUid: string, languagesArray: Language[]) => {
      const selectedPortal = portals.portals.find(
        (p) => p.portalUid === portalUid,
      );
      if (selectedPortal) {
        const updatedPortal = {
          ...selectedPortal,
          portalLanguages: languagesArray,
        };
        if (portals.selectedPortal?.portalUid !== updatedPortal.portalUid) {
          selectPortal(updatedPortal);
        }
      }
      const codeSet = new Set(
        languagesArray.map((lang) => lang.languageCode.toLowerCase()),
      );
      const hasLanguage = languagesArray.find(
        (lang) => lang.languageCode === languages.selectedLanguage,
      );
      if (!hasLanguage) {
        languagesDispatch({
          type: "SET_SELECTED_LANGUAGE",
          payload: "en",
        });
      }
      const stateLanguages = languages.languages.filter((lang) =>
        codeSet.has(lang.langugageCode.toLowerCase()),
      );
      languagesDispatch({
        type: "SET_PORTAL_DESIGN_LANGUAGES",
        payload: stateLanguages,
      });
      const target = getFirstAccessiblePortalConfigPath(
        auth.user,
        portals.selectedPortal,
        portalUid,
      );
      if (target) navigate(target);
    },
    [
      portals.portals,
      portals.selectedPortal,
      selectPortal,
      languages.selectedLanguage,
      languages.languages,
      languagesDispatch,
      navigate,
      auth.user,
    ],
  );

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
    setPage(1);
  }, []);

  const handleSortChange = (orderBy: string, order: SortOrder) => {
    setSort({ orderBy, order });
    fetchPortals({
      resultType: PortalResultType.GRID,
      ...(debouncedSearchQuery ? { portalName: debouncedSearchQuery } : {}),
      sort: order,
      orderBy: orderBy,
      limit: limit,
      offset: (page - 1) * limit,
    });
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    const offset = (newPage - 1) * limit;
    fetchPortals({
      resultType: PortalResultType.GRID,
      ...(debouncedSearchQuery ? { portalName: debouncedSearchQuery } : {}),
      ...(sort ? { sort: sort.order, orderBy: sort.orderBy } : {}),
      limit: limit,
      offset: offset,
    });
  };

  const handleRefresh = () => {
    if (loading) return;
    fetchPortals(
      {
        resultType: PortalResultType.GRID,
        ...(debouncedSearchQuery ? { portalName: debouncedSearchQuery } : {}),
        limit,
        offset: (page - 1) * limit,
        ...(sort ? { sort: sort.order, orderBy: sort.orderBy } : {}),
      },
      { surfaceErrors: true },
    );
    fetchPortalsContext();
  };

  const handleModalConfirm = async () => {
    try {
      if (selectedPortal) {
        const portal = portalsArray.find(
          (p) => p.portalUid === selectedPortal.portalUid,
        );
        if (portal) {
          setModalLoading(true);
          if (authoringToolModalOpen) {
            const updateBody: UpdatePortalBodyParams = {
              authoringTool: !portal.authoringTool,
              portalLanguages: portal.portalLanguages,
              name: portal.name,
              customTranslation: portal.customTranslation,
              spoofEmail: portal.spoofingEmail || undefined,
              url: portal.url,
              urlName: portal.urlName,
            };
            await updatePortal(selectedPortal.portalUid, updateBody);
          } else {
            const updateBody: UpdatePortalBodyParams = {
              authoringTool,
              portalLanguages: languagesArray,
              name: portal.name,
              customTranslation: portal.customTranslation,
              spoofEmail: portal.spoofingEmail || undefined,
              url: portal.url,
              urlName: portal.urlName,
            };
            await updatePortal(selectedPortal.portalUid, updateBody);
          }
          //  Replace reload with refetch
          await fetchPortals({ resultType: PortalResultType.GRID, limit });
          fetchLanguages();
          fetchPortalsContext();
        }
      }
    } catch (error: any) {
      console.log("Error:", error);
      showToast(extractErrorMessage(error, "Failed to create portal"), "error");
    } finally {
      setModalLoading(false);
    }
  };

  const confirmModalButtonClick = () => {
    if (selectedPortal) {
      setAuthoringToolModalOpen(false);
      setSelectedPortal(null);
    }
  };

  const addNewPortal = async () => {
    try {
      if (!name.trim()) {
        setNameError("Portal name is required.");
      }
      if (nameError) {
        if (!name.length) {
          showToast("Portal Name is required", "error");
        }
        showToast("Invalid portal name.", "error");
      }
      if (!subDomain.trim()) {
        setSubDomainError("Sub domain is required.");
      }
      if (subDomainError) {
        if (!subDomain.length) {
          showToast("Sub domain is required", "error");
        }
        showToast("Invalid subdomain format.", "error");
      }

      if (!isParent && !isSub) {
        setPortalTypeError("You must select a type");
      }
      if (maxRoleLevel === "") {
        setMaxRoleLevelError("Maximum role access level is required.");
      } else {
        setMaxRoleLevelError("");
      }
      if (
        !name.trim() ||
        !REGEX_ALPHANUMERIC.test(name) ||
        !subDomain.trim() ||
        !SUB_DOMAIN_REGEX.test(subDomain) ||
        (!isParent && !isSub) ||
        maxRoleLevel === ""
      )
        return;

      if (isParent) {
        const validatedSubPortals = subPortals.map((sp) => {
          const nameError =
            sp.name.trim() === "" ? "Portal name is required." : "";
          const subDomainError =
            sp.subDomain.trim() === "" ? "Sub domain is required." : "";
          return { ...sp, nameError, subDomainError };
        });

        setSubPortals(validatedSubPortals);
      }
      if (languagesArray.length === 0) {
        setLanguagesError("At least one language must be selected.");
        return;
      }

      setButtonLoading(true);
      const subportalsArray: AddSubPortals[] = subPortals.map((s) => {
        const formatedUrl = s.subDomain.toLowerCase().replace(/\s+/g, "_");
        const fullUrl = /^https?:\/\//i.test(formatedUrl)
          ? formatedUrl
          : `https://${formatedUrl}`;
        return {
          name: s.name,
          urlName: fullUrl,
          url: fullUrl,
        };
      });
      if (selectedParentId) {
        const formatedUrl = subDomain.toLowerCase().replace(/\s+/g, "_");
        const fullUrl = /^https?:\/\//i.test(formatedUrl)
          ? formatedUrl
          : `https://${formatedUrl}`;
        const createBody: CreatePortalBodyParams = {
          parentPortalUid: selectedParentId,
          name,
          portalLanguages: languagesArray,
          subPortals: subportalsArray,
          url: fullUrl,
          urlName: fullUrl,
          authoringTool,
          customTranslation,
          maxRoleLevel,
          spoofEmail: spoofingEmail || undefined,
        };

        await addPortal(createBody);

        const portalsDropdown = await getPortals({
          resultType: PortalResultType.DDL,
        });
        if (portalsDropdown) {
          dispatch({ type: "SET_PORTALS", payload: portalsDropdown });
        }
        await fetchPortals({
          resultType: PortalResultType.GRID,
          limit,
          offset: Math.floor(portalsResponse?.count! / limit) * limit,
        });
      } else {
        const formatedUrl = subDomain.toLowerCase().replace(/\s+/g, "_");
        const fullUrl = /^https?:\/\//i.test(formatedUrl)
          ? formatedUrl
          : `https://${formatedUrl}`;
        const createBody: CreatePortalBodyParams = {
          name,
          portalLanguages: languagesArray,
          subPortals: subportalsArray,
          url: fullUrl,
          urlName: fullUrl,
          authoringTool,
          customTranslation,
          maxRoleLevel,
          spoofEmail: spoofingEmail || undefined,
        };
        await addPortal(createBody);

        const portals = await getPortals({
          resultType: PortalResultType.DDL,
        });
        if (portals) {
          dispatch({ type: "SET_PORTALS", payload: portals });
        }
        await fetchPortals({
          resultType: PortalResultType.GRID,
          limit,
          offset: Math.floor(portalsResponse?.count! / limit) * limit,
        });

        setPage(Math.max(1, Math.ceil((portalsResponse?.count! + 1) / limit)));
      }

      setName("");
      setSubDomain("");
      setSubPortals([]);
      setSelectedParentId(null);
      setAuthoringTool(false);
      setCustomTranslation(false);
      setSpoofingEmail("");
      setMaxRoleLevel("");
      setMaxRoleLevelError("");
      setLanguagesArray([{ languageCode: "en" }]);
      setIsSub(false);
      setIsParent(false);
      setOpenModal(false);
    } catch (error: any) {
      console.log("Error:", error);
      showToast(extractErrorMessage(error, "Failed to add portal"), "error");
    } finally {
      setButtonLoading(false);
    }
  };
  const handleClose = () => {
    setNameError("");
    setSubDomainError("");
    setPortalTypeError("");
    setName("");
    setSubDomain("");
    setSubPortals([]);
    setSelectedParentId(null);
    setAuthoringTool(false);
    setCustomTranslation(false);
    setSpoofingEmail("");
    setSpoofingEmailError("");
    setMaxRoleLevel("");
    setMaxRoleLevelError("");
    setLanguagesArray([{ languageCode: "en" }]);
    setIsSub(false);
    setIsParent(false);
    setOpenModal(false);
  };

  usePageMetadata(
    {
      title: "Portal configuration",
      breadcrumbs: [],
    },

    [location.pathname],
  );

  const handleOpenLanguagesModal = useCallback((portal: Portal) => {
    setSelectedPortal(portal);
    setLanguagesArray([...portal.portalLanguages]);
    setLanguagesError("");
    setLanguagesModal(true);
  }, []);

  // Roles that could add portals, edit languages etc..
  // main admin and portal admin
  const hasAdminPrivileges = hasAdminAccess(auth.user, portals.selectedPortal);

  const userRoles: Roles[] = auth.user
    ? Array.isArray(auth.user.roles)
      ? auth.user.roles
      : [auth.user.roles]
    : [];
  const userLevel = getUserHighestRoleLevel(userRoles);

  const nonActiveSubPortalUids = useMemo(() => {
    const set = new Set<string>();
    portalsArray.forEach((portal) => {
      if (portal.status !== "Active") {
        (portal.subPortals ?? []).forEach((sub) => set.add(sub.portalUid));
      }
    });
    return set;
  }, [portalsArray]);

  const columns: ColumnDef<Portal>[] = useMemo(() => {
    const renderLanguageBadges = (portal: Portal) => {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            gap: "6px",
          }}
        >
          {portal.portalLanguages
            .slice(0, portal.portalLanguages.length > 3 ? 2 : 3)
            .map((lang, index) => {
              const details = getLanguageDetails(lang.languageCode);
              return details ? (
                <Box
                  key={lang.languageCode}
                  onClick={() => {
                    if (!hasAdminPrivileges) return;
                    handleOpenLanguagesModal(portal);
                  }}
                  sx={{
                    display: "flex",
                    gap: "4px",
                    alignItems: "center",
                    borderRadius: "999px",
                    cursor: hasAdminPrivileges ? "pointer" : "not-allowed",
                    border:
                      index === 0
                        ? `1px solid #E9D7FE`
                        : index === 1
                          ? `1px solid #B2DDFF`
                          : `1px solid #C7D7FE`,
                    backgroundColor:
                      index === 0
                        ? "#F9F5FF"
                        : index === 1
                          ? "#EFF8FF"
                          : "#EEF4FF",
                    color:
                      index === 0
                        ? "#6941C6"
                        : index === 1
                          ? "#175CD3"
                          : "#3538CD",
                    padding: "2px 4px 2px 2px",
                    height: "100%",
                    opacity: hasAdminPrivileges ? 1 : 0.6,
                    "&:hover": hasAdminPrivileges
                      ? {
                          cursor: "pointer",
                          backgroundColor:
                            index === 0
                              ? "#F4EBFF"
                              : index === 1
                                ? "#E0F2FF"
                                : "#E5ECFF",
                        }
                      : undefined,
                  }}
                >
                  <img
                    style={{
                      border: `1px solid ${gray[300]}`,
                      borderRadius: "999px",
                      objectFit: "cover",
                      cursor: hasAdminPrivileges ? "pointer" : "not-allowed",
                    }}
                    src={details.flag}
                    width={20}
                    height={20}
                    alt="flag"
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "0.75rem",
                      cursor: hasAdminPrivileges ? "pointer" : "not-allowed",
                    }}
                  >
                    {details.name}
                  </Typography>
                </Box>
              ) : null;
            })}

          {portal.portalLanguages.length > 3 && (
            <Box
              onClick={() => {
                if (!hasAdminPrivileges) return;
                handleOpenLanguagesModal(portal);
              }}
              sx={{
                minWidth: 28,
                height: 28,
                borderRadius: "50%",
                backgroundColor: "#EFF8FF",
                border: `1px solid #B2DDFF`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.75rem",
                color: "#175CD3",
                cursor: hasAdminPrivileges ? "pointer" : "not-allowed",
                fontWeight: 500,
                opacity: hasAdminPrivileges ? 1 : 0.6,
                "&:hover": hasAdminPrivileges
                  ? {
                      cursor: "pointer",
                      backgroundColor: "#E0F2FF",
                    }
                  : undefined,
              }}
            >
              +{portal.portalLanguages.length - 2}
            </Box>
          )}
        </Box>
      );
    };

    return [
      {
        id: "name",
        label: "Name",
        sortable: true,
        width: 400,
        render: (portal: Portal) => {
          const isActive = portal.status === "Active";
          return (
            <Typography
              onClick={() => {
                if (!isActive) return;
                handleEditClick(portal.portalUid, portal.portalLanguages);
              }}
              sx={{
                color: brand[600],
                fontWeight: 600,
                cursor: isActive ? "pointer" : "default",
                "&:hover": isActive
                  ? { textDecoration: `underline`, color: brand[700] }
                  : undefined,
              }}
            >
              {portal.name}
            </Typography>
          );
        },
      },
      {
        id: "url",
        label: "URL",
        sortable: true,
        render: (portal: Portal) => (
          <Typography
            sx={{
              color: brand[600],
              fontWeight: 600,
              textDecoration: `underline`,
            }}
          >
            {portal.url}
          </Typography>
        ),
      },
      {
        id: "copy",
        label: "",
        render: (portal: Portal) => (
          <img
            style={{ cursor: "pointer" }}
            onClick={() => {
              navigator.clipboard.writeText(portal.url).catch((err) => {
                console.error("Clipboard copy failed", err);
              });
            }}
            src={copy}
            width={16}
            alt="copy"
          />
        ),
      },
      {
        id: "languages",
        label: "Languages",
        minWidth: 200,
        maxWidth: 270,
        render: (portal: Portal) => renderLanguageBadges(portal),
      },
      {
        id: "authoringTool",
        label: "Authoring Tool",
        sortable: true,
        render: (portal: Portal) => (
          <Switch
            checked={portal.authoringTool}
            disabled={portal.isSubportal || !hasAdminPrivileges}
            onClick={() => {
              if (!portal.isSubportal) {
                setSelectedPortal(portal);
                setAuthoringToolModalOpen(true);
              }
            }}
          />
        ),
      },
      {
        id: "status",
        label: "Status",
        sortable: true,
        render: (portal: Portal) => (
          <Typography variant="body2">{portal.status ?? ""}</Typography>
        ),
      },
    ];
  }, [
    getLanguageDetails,
    handleEditClick,
    handleOpenLanguagesModal,
    hasAdminPrivileges,
  ]);

  const getNestedRows = (portal: Portal): Portal[] | undefined => {
    return portal.subPortals && portal.subPortals.length > 0
      ? portal.subPortals
      : undefined;
  };

  return (
    <Box>
      <>
        {!loaded ? (
          <Box
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            p={2}
            height={500}
          >
            <CircularProgress size={100} />
          </Box>
        ) : (
          <Paper sx={{ p: 0 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              p={2}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="h6">Portals</Typography>
                <Box
                  width={24}
                  height={24}
                  borderRadius="50%"
                  bgcolor="#EFF8FF"
                  border={"1px solid #B2DDFF"}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Typography color="#175CD3">
                    {portalsResponse?.count ?? 0}
                  </Typography>
                </Box>
              </Box>

              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  placeholder="Search"
                  variant="outlined"
                  size="small"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  sx={{
                    width: 300,
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "& .MuiInputBase-root": {
                      bgcolor: "#EAECF0",
                      borderRadius: "12px",
                    },
                  }}
                  slotProps={{
                    input: {
                      sx: {
                        backgroundColor: `${gray[100]} !important`,
                        borderRadius: "12px",
                      },
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    },
                    htmlInput: { maxLength: MAX_LENGTH_SHORT_LABEL },
                  }}
                />
                <Tooltip title="Refreshes grid data only, not the full page.">
                  <span>
                    <IconButton
                      aria-label="Refresh"
                      disabled={loading}
                      onClick={handleRefresh}
                    >
                      {loading ? (
                        <CircularProgress size={20} />
                      ) : (
                    <img src={refreshIcon} alt="refresh" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
                {hasAdminPrivileges && (
                  <Button
                    onClick={() => {
                      setLanguagesArray([{ languageCode: "en" }]);
                      setMaxRoleLevel(RoleLevel.ADMIN);
                      setOpenModal(true);
                    }}
                    sx={{
                      px: 2,
                      borderRadius: "12px",
                      backgroundColor: `${brand[600]} !important`,
                      color: `white`,
                      "&:hover": {
                        bgcolor: `${brand[700]} !important`,
                      },
                    }}
                  >
                    Add New Portal
                  </Button>
                )}
              </Stack>
            </Box>
            <DataTable
              data={portalsArray}
              columns={columns}
              totalCount={portalsResponse.count}
              page={page}
              rowsPerPage={limit}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsChange}
              sort={sort}
              onSortChange={handleSortChange}
              title={null}
              actions={null}
              getRowId={(portal) => portal.portalUid}
              getNestedRows={getNestedRows}
              getRowSx={(portal) => {
                const isActive =
                  portal.status === "Active" &&
                  !nonActiveSubPortalUids.has(portal.portalUid);
                return {
                  ...(portal.isMain && {
                    backgroundColor: "#F9F5FF !important",
                  }),
                  ...(!isActive && {
                    opacity: 0.5,
                    pointerEvents: "none",
                  }),
                };
              }}
              getCellSx={(portal, _column, colIndex, depth = 0) => {
                if (colIndex === 0 && depth === 0) {
                  if (portal.isMain) {
                    return {
                      borderLeft: `3px solid ${brand[600]}`,
                    };
                  }
                  // Match main-portal content offset so name column aligns (main has 3px border + default padding)
                  return { paddingLeft: "19px" };
                }
                return {};
              }}
            />
          </Paper>
        )}
      </>
      <LanguagesModal
        languagesError={languagesError}
        handleLanguagesChange={handleLanguagesChange}
        languagesArray={languagesArray}
        languagesModal={languagesModal}
        setLanguagesModal={setLanguagesModal}
        selectedPortal={selectedPortal}
        setSelectedPortal={setSelectedPortal}
        setPortals={setPortalsArray}
        handleModalConfirm={handleModalConfirm}
        modalLoading={modalLoading}
        isSubPortal={selectedPortal?.isSubportal}
      />

      <AddNewPortalModal
        openModal={openModal}
        setOpenModal={setOpenModal}
        languagesArray={languagesArray}
        name={name}
        nameError={nameError}
        handleNameChange={handleNameChange}
        subDomain={subDomain}
        subDomainError={subDomainError}
        handleSubDomainChange={handleSubDomainChange}
        languagesError={languagesError}
        handleLanguagesChange={handleLanguagesChange}
        addNewPortal={addNewPortal}
        authoringTool={authoringTool}
        customTranslation={customTranslation}
        handleAddSubPortal={handleAddSubPortal}
        handleSwitchChange={handleSwitchChange}
        handleCustomTranslationChange={handleCustomTranslationChange}
        subPortals={subPortals}
        setSubPortals={setSubPortals}
        selectedParentId={selectedParentId}
        setSelectedParentId={setSelectedParentId}
        buttonLoading={buttonLoading}
        isParent={isParent}
        isSub={isSub}
        setIsParent={setIsParent}
        setIsSub={setIsSub}
        setAuthoringTool={setAuthoringTool}
        setCustomTranslation={setCustomTranslation}
        setLanguages={setLanguagesArray}
        portalTypeError={portalTypeError}
        setPortalTypeError={setPortalTypeError}
        handleClose={handleClose}
        portalsArray={portalsArray}
        spoofingEmail={spoofingEmail}
        spoofingEmailError={spoofingEmailError}
        handleSpoofingEmailChange={handleSpoofingEmailChange}
        setSpoofingEmail={setSpoofingEmail}
        maxRoleLevel={maxRoleLevel}
        setMaxRoleLevel={setMaxRoleLevel}
        maxRoleLevelError={maxRoleLevelError}
        setMaxRoleLevelError={setMaxRoleLevelError}
        userLevel={userLevel ?? undefined}
      />

      <ConfirmModal
        openConfirmModal={authoringToolModalOpen}
        setOpenConfirmModal={setAuthoringToolModalOpen}
        confirmButtonClick={confirmModalButtonClick}
        modalTitle="Third party authoring tool"
        modalSubtitle="Are you sure you want to turn it on/off?"
        handleModalConfirm={handleModalConfirm}
        modalLoading={modalLoading}
      />
    </Box>
  );
};
